/**
 * CelebrationOverlay — subtle confetti + hero banner + profile crown when the
 * logged-in user has a birthday / work anniversary / wedding anniversary today.
 *
 * Rules:
 *   - Confetti fires ONCE per day (localStorage-gated).
 *   - Banner is dismissible; dismissal persists per day.
 *   - Respects the workspace's opt-out preference set on their employee record.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import confetti from "canvas-confetti";
import { X } from "lucide-react";

const EMOJI = {
  birthday: "🎂",
  work_anniversary: "🚀",
  wedding_anniversary: "💐",
};

const HEADLINE = {
  birthday: (name) => `Happy Birthday, ${name} 🎉`,
  work_anniversary: (name, y) => `Cheers to ${name} — ${y ? `${y}-year ` : ""}work anniversary 🚀`,
  wedding_anniversary: (name) => `Anniversary wishes to ${name} 💐`,
};

export default function CelebrationOverlay() {
  const { activeWorkspaceId } = useAuth();
  const [mine, setMine] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const dateKey = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/celebrations/me`);
        setMine(data.mine || []);
      } catch { /* ok — silent */ }
    })();
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!mine.length) return;
    const dismissKey = `acquilix.celebration.dismiss.${dateKey}`;
    if (localStorage.getItem(dismissKey) === "1") setDismissed(true);
    const confettiKey = `acquilix.celebration.confetti.${dateKey}`;
    if (localStorage.getItem(confettiKey) === "1") return;
    // Fire subtle confetti once per day
    localStorage.setItem(confettiKey, "1");
    const durationMs = 2500;
    const end = Date.now() + durationMs;
    const colors = ["#f59e0b", "#ec4899", "#8b5cf6", "#22c55e", "#3b82f6", "#eab308"];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.9 },
        colors,
        scalar: 0.9,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.9 },
        colors,
        scalar: 0.9,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [mine, dateKey]);

  if (!mine.length || dismissed) return null;
  const primary = mine[0];
  const name = primary.name || "you";
  const headline = HEADLINE[primary.kind]
    ? HEADLINE[primary.kind](name, primary.years)
    : `Celebrating you today, ${name}!`;
  const emoji = EMOJI[primary.kind] || "🎉";
  return (
    <div
      className="border-b border-amber-300 relative"
      style={{
        background: "linear-gradient(90deg,#fef3c7,#fed7aa,#fbcfe8,#fef3c7)",
        color: "#7c2d12",
      }}
      data-testid="celebration-banner"
    >
      <div className="max-w-full px-6 py-3 flex items-center gap-3">
        <div className="text-2xl">{emoji}</div>
        <div className="flex-1">
          <div className="font-semibold text-sm">{headline}</div>
          <div className="text-xs opacity-80">
            The whole team is celebrating you today — check the bulletin board.
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.setItem(`acquilix.celebration.dismiss.${dateKey}`, "1");
            setDismissed(true);
          }}
          className="p-1.5 hover:bg-black/10 rounded"
          aria-label="Dismiss"
          data-testid="celebration-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
